import {v4 as uuid4} from "uuid"
import {ensureValidParameters} from "#graph/helpers/parameters"
import type {BaseGraphNodeDefinition, GetInputParameters, GetParameters} from "#graph/nodes/base/types"
import {truncateAt} from "@cm/utils"
import type {VersionChain} from "#graph/versioning/types"
import type {BaseGraphNodeStatic} from "#graph/nodes/graph/static-types"

export abstract class BaseGraphNode<out Definition extends BaseGraphNodeDefinition = BaseGraphNodeDefinition> {
    public static label?: string
    public static versionChain?: VersionChain

    instanceId = uuid4()
    public parameters: GetParameters<Definition>

    public constructor(parameters: GetInputParameters<Definition>, disableValidation?: boolean) {
        this.parameters = !disableValidation ? ensureValidParameters(this, parameters) : parameters
    }

    toJSON() {
        return {
            [`${this.nodeClass}<${this.instanceId}>`]: this.parameters,
        }
    }

    toString() {
        return `${this.nodeClass}<${this.instanceId}>: ${truncateAt(JSON.stringify(this.parameters))}`
    }

    get id() {
        return this.instanceId
    }

    // TypeScript thinks of this.constructor as a generic constructor function,
    // so we need to cast it to the correct type
    private _staticProperties = this.constructor as unknown as BaseGraphNodeStatic<Definition>

    get definition(): Definition {
        return this._staticProperties.definition
    }

    get paramsSchema(): Definition["validation"]["parameters"] {
        return this._staticProperties.definition.validation.parameters
    }

    get label() {
        return this._staticProperties.label
    }

    get nodeClass() {
        return this._staticProperties.nodeClass
    }

    get nodeVersion() {
        const versionChain: VersionChain = this._staticProperties.versionChain ?? []
        if (!Array.isArray(versionChain)) throw new Error(`Version chain should be an array, got ${versionChain}`)
        return versionChain.length
    }

    replaceParameters(newParameters: GetInputParameters<Definition>, disableValidation = false) {
        this.parameters = !disableValidation ? ensureValidParameters(this, newParameters) : newParameters
    }

    updateParameters(newParameters: Partial<GetInputParameters<Definition>>, disableValidation = false) {
        const mergedParameters = {...this.parameters, ...newParameters}
        this.replaceParameters(mergedParameters, disableValidation)
    }
}
